import { useCallback } from 'react';
import { useLocalStorage } from '../../../hooks/useLocalStorage';

export const DEFAULT_RASITUSLUOKKA = 2;

// Placeholder persistence — explicitly local-only for now (see the plan:
// this repo's standing convention is to pause before designing new backend
// schema/DDL and let that be spec'd separately). Each answer is stored as
// { code: { [optionId]: { rasitusluokka: 1|2|3 } } } — a building can
// genuinely have more than one variant of the same rakenneosa present
// (e.g. some walls in lautaverhous, others in tiiliverhous), so this is
// multi-select, not a single reference; and each selected option's
// rasitusluokka (exposure/usage class, 1 vaikea / 2 normaali / 3 kevyt) is
// its own value, not shared across the whole question — two walls of
// different materials on the same building can genuinely sit in different
// exposure conditions. Keyed by optionId, not label — a handful of
// rakenneosa (1131 Salaojat, 1251 Parvekkeet) repeat the exact same label
// twice, split only by era/code-compliance, so label alone can't identify
// an option uniquely; optionId (assigned at generation time) can.
//
// "Selected" is just "this optionId has an entry" — there's no separate
// id list to keep in sync with it.
//
// Still never a copy of the lifespan/interval/notes values themselves —
// just the chosen ids plus each one's exposure class — so resolving the
// one applicable lifespan is always a lookup through rtSectionTree's
// questionsByCode/resolveAnswer at read time (pick
// lifespanRL{rasitusluokka} off the resolved option). The shape below
// (answers map + pickRasitusluokka + answeredCount) is already exactly
// what a GET/PUT-backed hook would expose, so swapping this hook's
// internals for real API calls later touches no caller.
export function useRTKayttoikaVastaukset(kiinteistoId) {
  const [answers, setAnswers] = useLocalStorage(
    kiinteistoId ? `rt_kayttoika_vastaukset_${kiinteistoId}` : 'rt_kayttoika_vastaukset_unknown',
    {}
  );

  // The checkbox is the selection gate — toggling an unselected option on
  // defaults its rasitusluokka to 2 normaali (the common case; the pills
  // are there to override it, not to require a judgment call just to say
  // "yes, this applies"). Toggling a selected option off drops it
  // entirely, regardless of whatever class it was set to.
  const toggleOption = useCallback((code, optionId) => {
    setAnswers(prev => {
      const current = prev[code] ?? {};
      if (optionId in current) {
        const rest = { ...current };
        delete rest[optionId];
        if (Object.keys(rest).length === 0) {
          const next = { ...prev };
          delete next[code];
          return next;
        }
        return { ...prev, [code]: rest };
      }
      return { ...prev, [code]: { ...current, [optionId]: { rasitusluokka: DEFAULT_RASITUSLUOKKA } } };
    });
  }, [setAnswers]);

  // Once an option is selected (checkbox), the pills only ever change
  // which exposure class applies — deselecting is the checkbox's job now,
  // not the pills', so this always sets/overwrites rather than toggling
  // off when the same class is tapped again.
  const pickRasitusluokka = useCallback((code, optionId, rasitusluokka) => {
    setAnswers(prev => {
      const current = prev[code] ?? {};
      if (!(optionId in current)) return prev; // nothing to re-classify until it's selected
      return { ...prev, [code]: { ...current, [optionId]: { rasitusluokka } } };
    });
  }, [setAnswers]);

  const clearAnswer = useCallback((code) => {
    setAnswers(prev => {
      if (!(code in prev)) return prev;
      const next = { ...prev };
      delete next[code];
      return next;
    });
  }, [setAnswers]);

  return {
    answers,
    toggleOption,
    pickRasitusluokka,
    clearAnswer,
    answeredCount: Object.keys(answers).length,
  };
}
